import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type {
  ChannelRecord,
  RoutingSkuTargetRecord,
  SkuSupplyCandidate,
  SkuSupplyCandidateIncompatibleReason,
  UpstreamChannelRecord,
  UpstreamModelCapabilityRecord,
} from '@nadoutong/contracts';
import { listKeyBuckets } from '../api/keyBuckets.ts';
import { listBindings } from '../api/keyChannelBindings.ts';
import { useSkuSupplyCandidates } from '../api/skuSupplyCandidates.ts';
import { listAccounts } from '../api/upstreamAccounts.ts';
import { listUpstreamCapabilities } from '../api/upstreams.ts';
import { useUpstreamChannels, useUpstreamsList } from '../api/useUpstreams.ts';
import { useToast } from '../components/Toast.tsx';
import { formatLocalTimestamp } from '../components/formatters.ts';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { Button, Checkbox, Dialog, FormField, Input, Select } from '../ui/index.ts';
import { parseNonNegativeInteger, parseOptionalRetryOrder } from './tenantRoutingShared.ts';
import type { SkuOption } from './tenantRoutingShared.ts';
import { SMART_ROLES, isSmartRole } from './tenant-routing/smartRouting.ts';
import { LIVE_CAPABILITY_STATUSES, capabilityStatusLabel } from './capabilityStatus.ts';
import { DisabledNoteField } from './tenant-routing/DisabledNoteField.tsx';
import { disabledNoteForWire } from './tenant-routing/disabledNote.ts';

/**
 * Centered dialog for adding / editing one SKU pool target (epic #898 P5,
 * re-keyed from the epic #886 channel dialog). Owns the identity cascade
 * (upstream → account → key bucket → upstream channel) plus the
 * routability validation against the capability chain; the parent sheet
 * owns the actual per-SKU mutation. Capability pinning intentionally makes
 * this a single-SKU flow: sibling models require their own explicit pins.
 */

/** What the dialog hands back: identity + tuning, SKU fan-out decided by flag. */
export interface SkuTargetDialogSubmission {
  readonly capability_id: string;
  readonly capability_protocol: string;
  readonly upstream_id: string;
  readonly account_id: string;
  readonly key_bucket_id: string;
  readonly upstream_channel_id: string;
  readonly weight: number;
  readonly retry_order: number | null;
  readonly enabled: boolean;
  /**
   * L2 caps. Omitted = leave stored value unchanged. `null` = clear.
   * Empty input omits on create / when the record had no numeric cap;
   * empty input sends null only when editing a stored number.
   */
  readonly rpm_cap?: number | null;
  readonly tpm_cap?: number | null;
  /** Omitted when the operator did not set a role. */
  readonly smart_role?: 'self_pool' | 'external' | 'reserve' | null;
  /** Omitted when the operator did not set a score. Empty edit clears to null. */
  readonly smart_initial_score?: number | null;
  /**
   * Manual-disable note (0082). Sent only when the target is saved disabled;
   * blank is `null`. An enabled save omits it (the gateway clears it anyway).
   */
  readonly disabled_note?: string | null;
}

interface TargetDraft {
  readonly capability_id: string;
  readonly upstream_id: string;
  readonly account_id: string;
  readonly key_bucket_id: string;
  readonly upstream_channel_id: string;
  readonly weight: string;
  readonly retry_order: string;
  readonly enabled: boolean;
  readonly rpm_cap: string;
  readonly tpm_cap: string;
  readonly smart_role: string;
  readonly smart_initial_score: string;
  readonly disabled_note: string;
}

const EMPTY_DRAFT: TargetDraft = {
  capability_id: '',
  upstream_id: '',
  account_id: '',
  key_bucket_id: '',
  upstream_channel_id: '',
  weight: '100',
  retry_order: '',
  enabled: true,
  rpm_cap: '',
  tpm_cap: '',
  smart_role: '',
  smart_initial_score: '',
  disabled_note: '',
};

function draftFromTarget(target: RoutingSkuTargetRecord | null): TargetDraft {
  if (target === null) return EMPTY_DRAFT;
  return {
    capability_id: target.capability_id ?? '',
    upstream_id: target.upstream_id,
    account_id: target.account_id,
    key_bucket_id: target.key_bucket_id,
    upstream_channel_id: target.upstream_channel_id,
    weight: String(target.weight),
    retry_order: target.retry_order === null ? '' : String(target.retry_order),
    enabled: target.enabled,
    rpm_cap: target.rpm_cap === null || target.rpm_cap === undefined ? '' : String(target.rpm_cap),
    tpm_cap: target.tpm_cap === null || target.tpm_cap === undefined ? '' : String(target.tpm_cap),
    smart_role: target.smart_role ?? '',
    smart_initial_score: target.smart_initial_score === null || target.smart_initial_score === undefined
      ? ''
      : String(target.smart_initial_score),
    disabled_note: target.disabled_note ?? '',
  };
}

function platformChannelAliases(channel: ChannelRecord): ReadonlySet<string> {
  return new Set([channel.id, channel.slug, `${channel.vendor}_${channel.slug}`]);
}

function upstreamChannelMatchesPlatform(
  platformChannel: ChannelRecord,
  upstreamChannel: UpstreamChannelRecord,
): boolean {
  const aliases = platformChannelAliases(platformChannel);
  return aliases.has(upstreamChannel.channel_id) || aliases.has(upstreamChannel.channel_slug);
}

type CapPatch =
  | { kind: 'invalid' }
  | { kind: 'omit' }
  | { kind: 'set'; value: number | null };

function parseCapPatch(raw: string, previous: number | null | undefined): CapPatch {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return typeof previous === 'number' ? { kind: 'set', value: null } : { kind: 'omit' };
  }
  if (!/^\d+$/.test(trimmed)) return { kind: 'invalid' };
  const value = Number.parseInt(trimmed, 10);
  if (!Number.isInteger(value) || value < 1) return { kind: 'invalid' };
  // A legal number is always sent: a re-keyed target is INSERT+DELETE on the
  // backend, so omitting an unchanged cap would drop it on the new row.
  return { kind: 'set', value };
}

/** PUT only the cap keys the dialog decided to change. */
export function skuTargetCapBody(
  submission: Pick<SkuTargetDialogSubmission, 'rpm_cap' | 'tpm_cap'>,
): { readonly rpm_cap?: number | null; readonly tpm_cap?: number | null } {
  return {
    ...(submission.rpm_cap !== undefined ? { rpm_cap: submission.rpm_cap } : {}),
    ...(submission.tpm_cap !== undefined ? { tpm_cap: submission.tpm_cap } : {}),
  };
}

function formatUpstreamChannel(channel: { readonly channel_slug: string; readonly channel_id: string }): string {
  return `${channel.channel_slug} · ${channel.channel_id}`;
}

/**
 * Why a channel cannot be bound. The two server verdicts, plus the price gate
 * `CandidateSupplyRow` has always applied on the SKU detail page and this
 * dialog did not: `assertCapabilityPinCost` refuses a pin whose
 * (channel × physical model) has no `upstream_channel_model_costs` row, and
 * the candidate read reports that as a null multiplier. Offering such a
 * channel here produces a save that fails at the gate with no way for the
 * operator to see why beforehand.
 */
type ChannelBindBlockReason = SkuSupplyCandidateIncompatibleReason | 'unpriced';

/** Same copy the SKU detail supply panel shows for the same verdict. */
function bindBlockReasonKey(reason: ChannelBindBlockReason): I18nKey {
  if (reason === 'protocol_mismatch') return 'sku_detail.supply.reason.protocol_mismatch';
  if (reason === 'unpriced') return 'tenant_routing.default.not_routable_unpriced';
  return 'sku_detail.supply.reason.egress_incompatible';
}

/** The full bind gate: compatible AND priced. Either alone is a failed save. */
function candidateIsBindable(candidate: SkuSupplyCandidate | undefined): boolean {
  return candidate !== undefined && candidate.compatible && candidate.cost_multiplier !== null;
}

function candidateBlockReason(
  candidate: SkuSupplyCandidate | undefined,
): ChannelBindBlockReason | null {
  if (candidate === undefined) return null;
  if (!candidate.compatible) return candidate.incompatible_reason;
  return candidate.cost_multiplier === null ? 'unpriced' : null;
}

function upstreamKeyChannelHref({
  upstreamId,
  accountId,
  keyBucketId,
  addChannelId,
}: {
  readonly upstreamId: string;
  readonly accountId: string;
  readonly keyBucketId: string;
  readonly addChannelId: string | null;
}): string {
  const params = new URLSearchParams({ tab: 'accounts' });
  if (accountId.length > 0) params.set('accountId', accountId);
  if (keyBucketId.length > 0) params.set('keyBucketId', keyBucketId);
  if (addChannelId !== null && addChannelId.length > 0) params.set('addChannelId', addChannelId);
  return `/supply/upstreams/${encodeURIComponent(upstreamId)}?${params.toString()}`;
}

interface Props {
  readonly open: boolean;
  /** The SKU this dialog is scoped to (fixes vendor/model for routability). */
  readonly sku: SkuOption | null;
  /** The SKU's namespace channel — drives the key-channel prerequisite hint. */
  readonly namespaceChannel: ChannelRecord | null;
  /** Sibling SKU count in the namespace (incl. the scoped SKU). */
  readonly namespaceSkuCount: number;
  readonly target: RoutingSkuTargetRecord | null;
  readonly saving: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (submission: SkuTargetDialogSubmission) => void;
}

export function TenantRoutingTargetDialog({
  open,
  sku,
  namespaceChannel,
  namespaceSkuCount,
  target,
  saving,
  onClose,
  onSubmit,
}: Props): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { fetchImpl } = useSession();
  const upstreams = useUpstreamsList();
  const [draft, setDraft] = useState<TargetDraft>(EMPTY_DRAFT);

  useEffect(() => {
    if (open) {
      setDraft(draftFromTarget(target));
    }
  }, [open, target]);

  const accounts = useQuery({
    queryKey: ['tenant-routing', 'target-accounts', draft.upstream_id] as const,
    queryFn: async () => (await listAccounts(draft.upstream_id, fetchImpl)).accounts,
    enabled: open && draft.upstream_id.length > 0,
  });
  const keyBuckets = useQuery({
    queryKey: ['tenant-routing', 'target-key-buckets', draft.upstream_id] as const,
    queryFn: async () => (await listKeyBuckets(draft.upstream_id, fetchImpl)).key_buckets,
    enabled: open && draft.upstream_id.length > 0,
  });
  const keyChannelBindings = useQuery({
    queryKey: ['tenant-routing', 'target-key-channel-bindings', draft.upstream_id, draft.key_bucket_id] as const,
    queryFn: async () => (await listBindings(draft.upstream_id, draft.key_bucket_id, fetchImpl)).bindings,
    enabled: open && draft.upstream_id.length > 0 && draft.key_bucket_id.length > 0,
  });
  const upstreamChannels = useUpstreamChannels(open ? draft.upstream_id : '');
  const capabilities = useQuery({
    queryKey: ['tenant-routing', 'target-capabilities', draft.upstream_id] as const,
    queryFn: ({ signal }) => listUpstreamCapabilities(draft.upstream_id, fetchImpl, signal),
    enabled: open && draft.upstream_id.length > 0,
  });
  /**
   * The bind gate's own per-capability verdict (#1327 PR-3), which this
   * dialog reads instead of re-deriving.
   *
   * It used to derive routability as `capability.vendor === sku.vendor`, and
   * those two `vendor` columns are different naming spaces:
   * `upstream_model_capabilities.vendor` is the wire PROTOCOL carrier
   * (`anthropic` / `openai` / `gemini` / `xai`, written by
   * `vendorForProtocol`), while a SKU's vendor is its namespace's real vendor
   * (`deepseek`, `doubao`, `moonshot`, `qwen`, `xai`, …). The equality held
   * only when a vendor happened to be spelled like a protocol, so every other
   * namespace's channels rendered permanently disabled as "not routable" —
   * the same class of defect #1882 §2 removed from the capability-receipt
   * join. It also mis-passed in the other direction, offering channels whose
   * supply the canonical egress contract rejects.
   *
   * Candidacy depends on the live-capability chain and that egress contract
   * at once — the very predicate `validateCapabilityPin` enforces on write —
   * and the client cannot see the egress config, so the verdict is read from
   * the server rather than guessed.
   */
  const supplyCandidates = useSkuSupplyCandidates(open && sku !== null ? sku.skuId : null);
  const candidateByCapabilityId = useMemo(() => {
    const byId = new Map<string, SkuSupplyCandidate>();
    for (const candidate of supplyCandidates.data?.candidates ?? []) {
      byId.set(candidate.capability_id, candidate);
    }
    return byId;
  }, [supplyCandidates.data]);

  const activeUpstreams = useMemo(
    () => (upstreams.data ?? []).filter((u) => u.enabled && u.kind === 'forward'),
    [upstreams.data],
  );
  const enabledAccounts = useMemo(
    () => (accounts.data ?? []).filter((account) => account.enabled),
    [accounts.data],
  );
  const enabledKeyBuckets = useMemo(
    () => (keyBuckets.data ?? []).filter(
      (bucket) =>
        bucket.enabled &&
        (draft.account_id.length === 0 || bucket.account_id === draft.account_id),
    ),
    [draft.account_id, keyBuckets.data],
  );
  const upstreamChannelOptions = useMemo(
    () => keyChannelBindings.data ?? [],
    [keyChannelBindings.data],
  );
  /**
   * SKU routability mirrors the gateway PUT guard (#908): the identity
   * must carry a live capability (`passed` / `needs_probe` /
   * `probe_failed`) the bind gate would accept for this SKU. The physical
   * capability model is deliberately independent from the canonical SKU
   * model; the gateway validates the explicit cross-model trust contract.
   */
  const matchingCapabilities = useMemo(() => {
    const matches: UpstreamModelCapabilityRecord[] = [];
    if (
      sku === null ||
      draft.upstream_id.length === 0 ||
      draft.account_id.length === 0 ||
      draft.key_bucket_id.length === 0
    ) {
      return matches;
    }
    for (const capability of capabilities.data ?? []) {
      if (
        capability.upstream_id !== draft.upstream_id ||
        capability.account_id !== draft.account_id ||
        capability.key_bucket_id !== draft.key_bucket_id ||
        !candidateIsBindable(candidateByCapabilityId.get(capability.id)) ||
        capability.channel_id !== draft.upstream_channel_id ||
        !LIVE_CAPABILITY_STATUSES.has(capability.status)
      ) {
        continue;
      }
      matches.push(capability);
    }
    return matches.sort((a, b) =>
      (b.last_passed_at ?? '').localeCompare(a.last_passed_at ?? '') || a.id.localeCompare(b.id));
  }, [
    candidateByCapabilityId,
    capabilities.data,
    draft.account_id,
    draft.key_bucket_id,
    draft.upstream_id,
    draft.upstream_channel_id,
    sku,
  ]);
  /**
   * Which upstream channels of the selected key can be saved, plus the
   * server's reason for the ones that cannot. The reason is per capability
   * and the control is per channel, so a channel with no compatible
   * capability reports the first refusal seen on it.
   */
  const channelRoutability = useMemo(() => {
    const routable = new Set<string>();
    const reasons = new Map<string, ChannelBindBlockReason>();
    for (const capability of capabilities.data ?? []) {
      if (
        sku === null ||
        capability.upstream_id !== draft.upstream_id ||
        capability.account_id !== draft.account_id ||
        capability.key_bucket_id !== draft.key_bucket_id ||
        !LIVE_CAPABILITY_STATUSES.has(capability.status)
      ) {
        continue;
      }
      const candidate = candidateByCapabilityId.get(capability.id);
      if (candidateIsBindable(candidate)) {
        routable.add(capability.channel_id);
        continue;
      }
      const reason = candidateBlockReason(candidate);
      if (reason !== null && !reasons.has(capability.channel_id)) {
        reasons.set(capability.channel_id, reason);
      }
    }
    return { routable, reasons };
  }, [
    candidateByCapabilityId,
    capabilities.data,
    draft.account_id,
    draft.key_bucket_id,
    draft.upstream_id,
    sku,
  ]);
  const routableChannelIds = channelRoutability.routable;
  const selectedCapability = matchingCapabilities.find((capability) => capability.id === draft.capability_id) ?? null;
  const observedPinnedCapability = (capabilities.data ?? []).find(
    (capability) => capability.id === draft.capability_id,
  ) ?? null;
  const legacyUnpinned = target !== null && target.capability_id == null;
  const selectedCapabilityStale = !capabilities.isPending && draft.capability_id.length > 0 && selectedCapability === null;
  const platformMatchedUpstreamChannels = useMemo(
    () => (upstreamChannels.data ?? []).filter((upstreamChannel) =>
      upstreamChannel.enabled &&
      namespaceChannel !== null &&
      upstreamChannelMatchesPlatform(namespaceChannel, upstreamChannel)
    ),
    [namespaceChannel, upstreamChannels.data],
  );
  const missingPlatformMatchedKeyChannels = useMemo(() => {
    if (draft.key_bucket_id.length === 0) return [];
    const attachedChannelIds = new Set(upstreamChannelOptions.map((binding) => binding.channel_id));
    return platformMatchedUpstreamChannels.filter((upstreamChannel) => !attachedChannelIds.has(upstreamChannel.channel_id));
  }, [draft.key_bucket_id, platformMatchedUpstreamChannels, upstreamChannelOptions]);

  // Both reads feed the verdict, so "checked" must wait for both — a pending
  // candidate list would otherwise read as "nothing is routable".
  const routabilityLoading = (draft.upstream_id.length > 0 && capabilities.isPending) ||
    (sku !== null && supplyCandidates.isPending);
  /*
    A candidate read that FAILED is not an answer about supply.

    The dialog stays fail-closed either way — with no verdict nothing is
    offered, and a save the gate would refuse is worse than a save the operator
    has to retry. What must not stay the same is the sentence. "This key and
    channel have no verified model target" is a claim about the fleet, and
    derived from a 503 it sends the operator to re-discover and re-probe supply
    that is fine. The two situations look identical on screen otherwise: no
    options, no explanation.
  */
  const candidatesUnavailable = sku !== null && supplyCandidates.isError;
  const hasSelectedKey = draft.key_bucket_id.length > 0;
  const routabilityChecked = hasSelectedKey && !routabilityLoading;
  const hasAnyRoutableChannel = upstreamChannelOptions.some((binding) => routableChannelIds.has(binding.channel_id));
  const selectedChannelUnavailable = draft.upstream_channel_id.length > 0 &&
    routabilityChecked &&
    !routableChannelIds.has(draft.upstream_channel_id);
  const missingPlatformChannelBinding = hasSelectedKey &&
    !(upstreamChannels.isPending && draft.upstream_id.length > 0) &&
    missingPlatformMatchedKeyChannels.length > 0;
  const missingChannelToAdd = missingPlatformMatchedKeyChannels[0] ?? null;
  const loadingChannels = keyChannelBindings.isPending && draft.key_bucket_id.length > 0;
  const saveDisabled = saving ||
    routabilityLoading ||
    (routabilityChecked && !hasAnyRoutableChannel) ||
    selectedChannelUnavailable ||
    selectedCapability === null;

  function handleSubmit(): void {
    if (sku === null) return;
    const upstreamChannel = upstreamChannelOptions.find(
      (binding) => binding.channel_id === draft.upstream_channel_id,
    );
    if (
      upstreamChannel === undefined ||
      draft.upstream_id.length === 0 ||
      draft.account_id.length === 0 ||
      draft.key_bucket_id.length === 0 ||
      draft.upstream_channel_id.length === 0 ||
      selectedCapability === null
    ) {
      toast.error(t('tenant_routing.error.default_required'));
      return;
    }
    if (routabilityLoading) {
      toast.error(t('tenant_routing.error.default_routability_pending'));
      return;
    }
    if (!routableChannelIds.has(upstreamChannel.channel_id)) {
      toast.error(t('tenant_routing.error.default_not_routable'));
      return;
    }
    const weight = parseNonNegativeInteger(draft.weight);
    if (weight === null) {
      toast.error(t('tenant_routing.error.default_weight'));
      return;
    }
    const retryOrder = parseOptionalRetryOrder(draft.retry_order);
    if (retryOrder === undefined) {
      toast.error(t('tenant_routing.error.retry_order'));
      return;
    }
    const rpmCap = parseCapPatch(draft.rpm_cap, target?.rpm_cap);
    const tpmCap = parseCapPatch(draft.tpm_cap, target?.tpm_cap);
    if (rpmCap.kind === 'invalid' || tpmCap.kind === 'invalid') {
      toast.error(t('tenant_routing.error.cap'));
      return;
    }
    const scoreRaw = draft.smart_initial_score.trim();
    const score = scoreRaw.length === 0 ? null : Number(scoreRaw);
    if (score !== null && (!Number.isFinite(score) || score < 0 || score > 1)) {
      toast.error(t('tenant_routing.smart.error.score'));
      return;
    }
    const storedRole = target?.smart_role ?? '';
    const nextRole = isSmartRole(draft.smart_role) ? draft.smart_role : '';
    const storedScore = target?.smart_initial_score ?? null;
    onSubmit({
      capability_id: selectedCapability.id,
      capability_protocol: selectedCapability.probe_protocol ?? selectedCapability.protocol,
      upstream_id: draft.upstream_id,
      account_id: draft.account_id,
      key_bucket_id: draft.key_bucket_id,
      upstream_channel_id: upstreamChannel.channel_id,
      weight,
      retry_order: retryOrder,
      enabled: draft.enabled,
      ...(nextRole !== storedRole
        ? { smart_role: isSmartRole(nextRole) ? nextRole : null }
        : {}),
      ...(score !== storedScore ? { smart_initial_score: score } : {}),
      ...(rpmCap.kind === 'set' ? { rpm_cap: rpmCap.value } : {}),
      ...(tpmCap.kind === 'set' ? { tpm_cap: tpmCap.value } : {}),
      ...(draft.enabled ? {} : { disabled_note: disabledNoteForWire(draft.disabled_note) }),
    });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      size="lg"
      title={t(target === null
        ? 'tenant_routing.pool.dialog_title_new'
        : 'tenant_routing.pool.dialog_title_edit')}
      description={sku === null
        ? undefined
        : `${sku.skuName}${namespaceChannel === null ? '' : ` · ${namespaceChannel.display_name}`}`}
      closeLabel={t('common.close')}
      testId="tenant-routing-target-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={saveDisabled}
            loading={saving}
            data-testid="tenant-routing-target-save"
          >
            {t('tenant_routing.pool.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4" data-testid="tenant-routing-target-form">
        <FormField label={t('tenant_routing.default.upstream')} fieldId="tenant-routing-target-upstream">
          <Select
            id="tenant-routing-target-upstream"
            ariaLabel={t('tenant_routing.default.upstream')}
            value={draft.upstream_id.length > 0 ? draft.upstream_id : undefined}
            onValueChange={(upstreamId) => {
              setDraft((d) => ({
                ...d,
                upstream_id: upstreamId,
                account_id: '',
                key_bucket_id: '',
                upstream_channel_id: '',
                capability_id: '',
              }));
            }}
            options={activeUpstreams.map((upstream) => ({ value: upstream.id, label: upstream.name }))}
            testId="tenant-routing-target-upstream"
          />
        </FormField>
        <FormField label={t('tenant_routing.default.account')} fieldId="tenant-routing-target-account">
          <Select
            id="tenant-routing-target-account"
            ariaLabel={t('tenant_routing.default.account')}
            key={`account-${draft.upstream_id}`}
            value={draft.account_id.length > 0 ? draft.account_id : undefined}
            onValueChange={(accountId) => {
              setDraft((d) => ({
                ...d,
                account_id: accountId,
                key_bucket_id: '',
                upstream_channel_id: '',
                capability_id: '',
              }));
            }}
            disabled={draft.upstream_id.length === 0}
            options={enabledAccounts.map((account) => ({ value: account.id, label: account.label }))}
            testId="tenant-routing-target-account"
          />
        </FormField>
        <FormField label={t('tenant_routing.default.key_bucket')} fieldId="tenant-routing-target-key">
          <Select
            id="tenant-routing-target-key"
            ariaLabel={t('tenant_routing.default.key_bucket')}
            key={`bucket-${draft.upstream_id}-${draft.account_id}`}
            value={draft.key_bucket_id.length > 0 ? draft.key_bucket_id : undefined}
            onValueChange={(keyBucketId) => {
              setDraft((d) => ({ ...d, key_bucket_id: keyBucketId, upstream_channel_id: '', capability_id: '' }));
            }}
            disabled={draft.account_id.length === 0}
            options={enabledKeyBuckets.map((bucket) => ({ value: bucket.id, label: bucket.label }))}
            testId="tenant-routing-target-key"
          />
        </FormField>
        <FormField label={t('tenant_routing.default.upstream_channel')} fieldId="tenant-routing-target-upstream-channel">
          <div>
            <Select
              id="tenant-routing-target-upstream-channel"
              ariaLabel={t('tenant_routing.default.upstream_channel')}
              key={`channel-${draft.key_bucket_id}`}
              value={draft.upstream_channel_id.length > 0 ? draft.upstream_channel_id : undefined}
              onValueChange={(upstreamChannelId) => {
                setDraft((d) => ({ ...d, upstream_channel_id: upstreamChannelId, capability_id: '' }));
              }}
              disabled={draft.key_bucket_id.length === 0 || loadingChannels}
              options={upstreamChannelOptions.map((binding) => {
                const blocked = routabilityChecked && !routableChannelIds.has(binding.channel_id);
                const reason = channelRoutability.reasons.get(binding.channel_id);
                return {
                  value: binding.channel_id,
                  label: blocked
                    ? `${binding.channel_slug} · ${binding.channel_id} · ${t('tenant_routing.default.not_routable_option')}${reason === undefined ? '' : ` · ${t(bindBlockReasonKey(reason))}`}`
                    : `${binding.channel_slug} · ${binding.channel_id}`,
                  disabled: blocked,
                };
              })}
              testId="tenant-routing-target-upstream-channel"
            />
            {hasSelectedKey ? (
              <p className="mt-2 text-dense leading-5 text-paper-low" data-testid="tenant-routing-target-routability-hint">
                {routabilityLoading
                  ? t('tenant_routing.default.routability_loading')
                  : candidatesUnavailable
                    ? t('tenant_routing.default.routability_unavailable')
                    : hasAnyRoutableChannel
                      ? t('tenant_routing.default.routability_hint')
                      : t('tenant_routing.default.no_routable_channels')}
              </p>
            ) : null}
            {missingPlatformChannelBinding ? (
              <div
                className="mt-3 rounded-md bg-well px-3 py-2 text-dense leading-5 text-paper-low"
                data-testid="tenant-routing-target-prerequisites"
              >
                <p>
                  {t('tenant_routing.default.missing_key_channel', {
                    channel: `${namespaceChannel?.vendor ?? ''}/${namespaceChannel?.slug ?? ''}`,
                  })}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {missingPlatformMatchedKeyChannels.map((upstreamChannel) => (
                    <span
                      key={upstreamChannel.channel_id}
                      className="rounded-full bg-active px-2 py-0.5 font-mono text-micro text-paper-mid"
                    >
                      {formatUpstreamChannel(upstreamChannel)}
                    </span>
                  ))}
                </div>
                <Link
                  to={upstreamKeyChannelHref({
                    upstreamId: draft.upstream_id,
                    accountId: draft.account_id,
                    keyBucketId: draft.key_bucket_id,
                    addChannelId: missingChannelToAdd?.channel_id ?? null,
                  })}
                  className="mt-2 inline-flex text-micro font-semibold text-signal hover:underline"
                >
                  {t('tenant_routing.default.open_upstream')}
                </Link>
              </div>
            ) : null}
          </div>
        </FormField>
        <FormField
          label={t('tenant_routing.default.capability')}
          fieldId="tenant-routing-target-capability"
          hint={t('tenant_routing.default.capability_hint')}
        >
          <div>
            <Select
              id="tenant-routing-target-capability"
              ariaLabel={t('tenant_routing.default.capability')}
              key={`capability-${draft.upstream_id}-${draft.account_id}-${draft.key_bucket_id}-${draft.upstream_channel_id}`}
              value={draft.capability_id.length > 0 ? draft.capability_id : undefined}
              onValueChange={(capabilityId) => setDraft((d) => ({ ...d, capability_id: capabilityId }))}
              disabled={draft.upstream_channel_id.length === 0 || routabilityLoading}
              options={[
                ...(draft.capability_id.length > 0 && selectedCapability === null ? [{
                  value: draft.capability_id,
                  label: `${observedPinnedCapability?.upstream_model_name ?? target?.upstream_model_name ?? '—'} · ${draft.capability_id} · ${observedPinnedCapability?.probe_protocol ?? observedPinnedCapability?.protocol ?? target?.capability_protocol ?? '—'} · ${capabilityStatusLabel(observedPinnedCapability?.status ?? target?.capability_status ?? null, t)}`,
                  disabled: true,
                }] : []),
                ...matchingCapabilities.map((capability) => ({
                value: capability.id,
                label: `${capability.upstream_model_name} · ${capability.id} · ${capability.probe_protocol ?? capability.protocol} · ${capabilityStatusLabel(capability.status, t)}`,
                })),
              ]}
              testId="tenant-routing-target-capability"
            />
            {(legacyUnpinned && selectedCapability === null) || selectedCapabilityStale ? (
              <p className="mt-2 rounded border border-warn-line bg-warn-dim px-3 py-2 text-dense text-warn" role="alert" data-testid="tenant-routing-target-capability-warning">
                {legacyUnpinned
                  ? t('tenant_routing.default.capability_legacy_warning')
                  : `${t('tenant_routing.default.capability_stale_warning')} · ${capabilityStatusLabel(observedPinnedCapability?.status ?? target?.capability_status ?? null, t)}`}
              </p>
            ) : null}
            {(selectedCapability !== null || draft.capability_id.length > 0) && sku !== null ? (
              <div className="mt-3 rounded-lg border border-line-soft bg-well p-3" data-testid="tenant-routing-target-capability-evidence">
                <p className="m-0 font-mono text-dense text-paper">
                  {sku.skuName} → {selectedCapability?.upstream_model_name ?? observedPinnedCapability?.upstream_model_name ?? target?.upstream_model_name ?? '—'} · {draft.capability_id}
                </p>
                <p className="mb-0 mt-1 font-mono text-micro text-paper-low">
                  {target?.capability_id !== null && target?.capability_id !== undefined && target.capability_id !== draft.capability_id
                    ? `${target.upstream_model_name ?? '—'} · ${target.capability_id} → ${selectedCapability?.upstream_model_name ?? '—'} · ${draft.capability_id} · `
                    : ''}
                  {selectedCapability?.probe_protocol ?? selectedCapability?.protocol ?? observedPinnedCapability?.probe_protocol ?? observedPinnedCapability?.protocol ?? target?.capability_protocol ?? '—'}
                </p>
                {(observedPinnedCapability?.last_passed_at ?? selectedCapability?.last_passed_at ?? target?.capability_last_passed_at ?? null) !== null ? (
                  <p className="mb-0 mt-1 text-micro text-paper-low">
                    {t('tenant_routing.default.capability_last_passed', {
                      time: formatLocalTimestamp((observedPinnedCapability?.last_passed_at ?? selectedCapability?.last_passed_at ?? target?.capability_last_passed_at)!, { seconds: true }),
                    })}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        </FormField>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label={t('tenant_routing.default.weight')}
            fieldId="tenant-routing-target-weight"
            hint={t('tenant_routing.pool.weight_hint')}
          >
            <Input
              id="tenant-routing-target-weight"
              type="number"
              min={0}
              step={1}
              mono
              value={draft.weight}
              onChange={(event) => {
                const weight = event.currentTarget.value;
                setDraft((d) => ({ ...d, weight }));
              }}
              data-testid="tenant-routing-target-weight"
            />
          </FormField>
          <FormField
            label={t('tenant_routing.pool.retry_order')}
            fieldId="tenant-routing-target-retry-order"
            hint={t('tenant_routing.pool.retry_order_hint')}
          >
            <Input
              id="tenant-routing-target-retry-order"
              type="number"
              min={1}
              step={1}
              mono
              placeholder={t('tenant_routing.pool.retry_order_auto')}
              value={draft.retry_order}
              onChange={(event) => {
                const retryOrder = event.currentTarget.value;
                setDraft((d) => ({ ...d, retry_order: retryOrder }));
              }}
              data-testid="tenant-routing-target-retry-order"
            />
          </FormField>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label={t('tenant_routing.target.rpm_cap')}
            fieldId="tenant-routing-target-rpm-cap"
            hint={t('tenant_routing.target.rpm_cap_hint')}
          >
            <Input
              id="tenant-routing-target-rpm-cap"
              type="number"
              min={1}
              step={1}
              mono
              value={draft.rpm_cap}
              onChange={(event) => {
                const rpmCap = event.currentTarget.value;
                setDraft((d) => ({ ...d, rpm_cap: rpmCap }));
              }}
              data-testid="tenant-routing-target-rpm-cap"
            />
          </FormField>
          <FormField
            label={t('tenant_routing.target.tpm_cap')}
            fieldId="tenant-routing-target-tpm-cap"
            hint={t('tenant_routing.target.tpm_cap_hint')}
          >
            <Input
              id="tenant-routing-target-tpm-cap"
              type="number"
              min={1}
              step={1}
              mono
              value={draft.tpm_cap}
              onChange={(event) => {
                const tpmCap = event.currentTarget.value;
                setDraft((d) => ({ ...d, tpm_cap: tpmCap }));
              }}
              data-testid="tenant-routing-target-tpm-cap"
            />
          </FormField>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label={t('tenant_routing.smart.role')}
            fieldId="tenant-routing-target-smart-role"
          >
            <Select
              id="tenant-routing-target-smart-role"
              ariaLabel={t('tenant_routing.smart.role')}
              value={draft.smart_role.length > 0 ? draft.smart_role : undefined}
              placeholder={t('tenant_routing.smart.role.unknown')}
              onValueChange={(smartRole) => setDraft((d) => ({ ...d, smart_role: smartRole }))}
              options={SMART_ROLES.map((role) => ({
                value: role,
                label: t(
                  role === 'self_pool'
                    ? 'tenant_routing.smart.role.self_pool'
                    : role === 'reserve'
                      ? 'tenant_routing.smart.role.reserve'
                      : 'tenant_routing.smart.role.external',
                ),
              }))}
              testId="tenant-routing-target-smart-role"
            />
          </FormField>
          <FormField
            label={t('tenant_routing.smart.initial_score')}
            fieldId="tenant-routing-target-smart-score"
            hint={t('tenant_routing.smart.initial_score_hint')}
          >
            <Input
              id="tenant-routing-target-smart-score"
              mono
              inputMode="decimal"
              value={draft.smart_initial_score}
              onChange={(event) => {
                const smartInitialScore = event.currentTarget.value;
                setDraft((d) => ({ ...d, smart_initial_score: smartInitialScore }));
              }}
              data-testid="tenant-routing-target-smart-score"
            />
          </FormField>
        </div>
        <Checkbox
          checked={draft.enabled}
          onCheckedChange={(enabled) => setDraft((d) => ({ ...d, enabled }))}
          label={t('tenant_routing.default.enabled')}
          testId="tenant-routing-target-enabled"
        />
        {draft.enabled ? null : (
          <DisabledNoteField
            value={draft.disabled_note}
            onChange={(disabledNote) => setDraft((d) => ({ ...d, disabled_note: disabledNote }))}
            fieldId="tenant-routing-target-disabled-note"
            testId="tenant-routing-target-disabled-note"
          />
        )}
        {target === null && namespaceSkuCount > 1 ? (
          <p className="m-0 text-label text-paper-low">
            {t('tenant_routing.sku_pools.capability_single_sku')}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
